import React from 'react';
import { formatFilterPrice, Icon, IconEnum } from '@investoro/core';
import styled from 'styled-components';
import type { HeroSectionDto } from 'utils/types/AiPredictorModels';

import BargainBadge from 'components/valuation/BargainBadge';
import { CheckboxIcon, FeatureChip, ParamChip } from 'components/valuation/common';

const HeroCard = styled.div<{ isPdf?: boolean }>`
    width: 100%;
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: ${({ isPdf }) => isPdf ? '20px 24px' : '28px 32px'};

    @media (max-width: 768px) {
        padding: 20px;
    }
`;

const HeroTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;
`;

const AddressBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
`;

const AddressLabel = styled.span`
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
    font-weight: 500;
`;

const AddressText = styled.div`
    display: flex;
    gap: 8px;
    font-size: 18px;
    font-weight: 700;
    color: var(--color-primary);
    margin: 0;
    line-height: 1.3;
`;

const PdfButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: transparent;
    border: 2px solid var(--color-primary);
    border-radius: 8px;
    color: var(--color-primary);
    font-size: var(--font-size-body-3);
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;

    &:hover {
        background: var(--color-primary);
        color: var(--color-white);
        img {
            filter: invert(1);
        }
    }

    @media (max-width: 480px) {
        display: none;
    }
`;

const PropertyParamsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 0;
    border-top: 1px solid var(--color-alt-second);
    border-bottom: 1px solid var(--color-alt-second);
    margin-bottom: 30px;
`;

const HeroMetricsRow = styled.div`
    margin-top: 6px;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 24px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const ScoreBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
`;

const ScoreCircle = styled.div<{ score: number }>`
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: conic-gradient(
        var(--color-alt) ${({ score }) => score * 10}%,
        var(--color-alt-second) ${({ score }) => score * 10}%
    );
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
        content: '';
        position: absolute;
        width: 76px;
        height: 76px;
        border-radius: 50%;
        background: var(--color-white);
    }
`;

const ScoreInner = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1;
`;

const ScoreNumber = styled.span`
    font-size: 28px;
    font-weight: 700;
    color: var(--color-primary);
`;

const ScoreMax = styled.span`
    font-size: 11px;
    color: var(--color-primary-70);
    font-weight: 500;
`;

const ScoreLabel = styled.span`
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
    font-weight: 500;
    text-align: center;
    max-width: 100px;
`;

const PriceRangesBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 140px;

    @media (max-width: 768px) {
        padding: 0;
    }
`;

const PriceRangesTitle = styled.div`
    font-weight: 600;
    font-size: var(--font-size-body-2);
    margin-bottom: 28px;
    color: var(--color-primary-70);
    text-align: center;

    @media (max-width: 768px) {
        font-size: var(--font-size-body-2);
        text-align: center;
    }
`;

const PriceRangeValue = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: var(--color-primary);
    line-height: 1.2;
    display: block;

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const PriceRangeSub = styled.span`
    font-size: 11px;
    color: var(--color-primary-70);
    font-weight: 500;
    display: block;
`;

const PriceBarTrack = styled.div`
    height: 8px;
    background: var(--color-alt-second-10);
    border-radius: 4px;
    position: relative;
    margin: 14px 0 0;
`;

const PriceBarDot = styled.div<{ side: 'left' | 'right' }>`
    width: 18px;
    height: 18px;
    background: var(--color-white);
    border: 3px solid var(--color-alt);
    border-radius: 50%;
    position: absolute;
    top: 50%;
    ${({ side }) => side === 'left'
        ? 'left: 25%; transform: translate(-50%, -50%);'
        : 'right: 25%; transform: translate(50%, -50%);'}
`;

const PriceMinMaxRow = styled.div`
    display: flex;
`;

const PriceMinMaxGroup = styled.div`
    flex: 1;
    text-align: center;
`;

const PriceRangeSubLabel = styled.span`
    font-size: 11px;
    color: var(--color-primary-70);
    font-weight: 500;
    display: block;
    margin-top: 2px;
`;

const ScoreCircleSvg = ({ score }: { score: number }) => {
    const radius = 44;
    const circumference = 2 * Math.PI * radius;
    const dashoffset = circumference * (1 - score / 10);

    return (
        <svg width="100" height="100" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={radius} fill="none" stroke="var(--color-alt-second)" strokeWidth="8" />
            <circle
                cx="50" cy="50" r={radius} fill="none"
                stroke="var(--color-alt)" strokeWidth="8"
                strokeDasharray={circumference}
                strokeDashoffset={dashoffset}
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
            />
            <text x="50" y="46" textAnchor="middle" fontSize="22" fontWeight="700" fill="var(--color-primary)" dominantBaseline="middle">
                {score.toFixed(1)}
            </text>
            <text x="50" y="63" textAnchor="middle" fontSize="10" fontWeight="500" fill="var(--color-primary-70)">
                / 10
            </text>
        </svg>
    );
};

interface ValuationHeroCardProps {
    hero: HeroSectionDto | undefined;
    subjectAddress: string | undefined;
    onExportPdf?: () => void;
    isExportingPdf?: boolean;
    isPdf?: boolean;
}

const ValuationHeroCard = ({ hero, subjectAddress, onExportPdf, isExportingPdf = false, isPdf }: ValuationHeroCardProps) => {
    const params = hero?.propertyParams;
    const hasPriceRanges = hero && (hero.estimatedPriceTotalMin != null || hero.estimatedPricePerSqmMin != null);

    return (
        <HeroCard isPdf={isPdf}>
            <HeroTopRow>
                <AddressBlock>
                    <AddressLabel>Wyceniana nieruchomość</AddressLabel>
                    <AddressText>
                        <Icon icon={IconEnum.MAP_PIN} />
                        {' '}{subjectAddress}
                    </AddressText>
                </AddressBlock>
                {!isPdf && onExportPdf &&
                    <PdfButton type="button" onClick={onExportPdf} disabled={isExportingPdf}>
                        <Icon icon={IconEnum.SAVE} />
                        {isExportingPdf ? 'Eksportowanie...' : 'Eksportuj do PDF'}
                    </PdfButton>
                }
            </HeroTopRow>

            {params &&
                <PropertyParamsRow>
                    {params.area != null &&
                        <ParamChip>{params.area} m²</ParamChip>}
                    {params.numberOfRooms != null &&
                        <ParamChip>{params.numberOfRooms} {params.numberOfRooms === 1 ? 'pokój' : 'pokoje'}</ParamChip>}
                    {params.floor != null &&
                        <ParamChip>Piętro {params.floor}</ParamChip>}
                    {params.builtYear != null &&
                        <ParamChip>Rok {params.builtYear}</ParamChip>}
                    {params.hasElevator != null &&
                        <FeatureChip active={params.hasElevator}>
                            <CheckboxIcon active={params.hasElevator}>{params.hasElevator ? '✓' : '−'}</CheckboxIcon>
                            Winda
                        </FeatureChip>}
                    {params.hasBalcony != null &&
                        <FeatureChip active={params.hasBalcony}>
                            <CheckboxIcon active={params.hasBalcony}>{params.hasBalcony ? '✓' : '−'}</CheckboxIcon>
                            Balkon
                        </FeatureChip>}
                    {params.hasParkingSpace != null &&
                        <FeatureChip active={params.hasParkingSpace}>
                            <CheckboxIcon active={params.hasParkingSpace}>{params.hasParkingSpace ? '✓' : '−'}</CheckboxIcon>
                            Miejsce postojowe
                        </FeatureChip>}
                    {params.hasStorageRoom != null &&
                        <FeatureChip active={params.hasStorageRoom}>
                            <CheckboxIcon active={params.hasStorageRoom}>{params.hasStorageRoom ? '✓' : '−'}</CheckboxIcon>
                            Komórka lokatorska
                        </FeatureChip>}
                </PropertyParamsRow>
            }

            {(hero?.attractivenessScore != null || hasPriceRanges) &&
                <HeroMetricsRow>
                    {hero?.attractivenessScore != null &&
                        <ScoreBlock>
                            {isPdf
                                ? <ScoreCircleSvg score={hero.attractivenessScore} />
                                : <ScoreCircle score={hero.attractivenessScore}>
                                    <ScoreInner>
                                        <ScoreNumber>{hero.attractivenessScore.toFixed(1)}</ScoreNumber>
                                        <ScoreMax>/ 10</ScoreMax>
                                    </ScoreInner>
                                </ScoreCircle>
                            }
                            <ScoreLabel>Wskaźnik atrakcyjności</ScoreLabel>
                            {hero.marketBargainLabel &&
                                <BargainBadge label={hero.marketBargainLabel} />
                            }
                        </ScoreBlock>
                    }

                    {hasPriceRanges &&
                        <PriceRangesBlock>
                            <PriceRangesTitle>Szacowana cena sprzedaży mieszkania</PriceRangesTitle>
                            <PriceMinMaxRow>
                                <PriceMinMaxGroup>
                                    {hero.estimatedPriceTotalMin != null &&
                                        <PriceRangeValue>{formatFilterPrice(hero.estimatedPriceTotalMin)} zł</PriceRangeValue>}
                                    {hero.estimatedPricePerSqmMin != null &&
                                        <PriceRangeSub>{formatFilterPrice(hero.estimatedPricePerSqmMin)} zł / m²</PriceRangeSub>}
                                    <PriceRangeSubLabel>Minimalna cena sprzedaży</PriceRangeSubLabel>
                                </PriceMinMaxGroup>
                                <PriceMinMaxGroup align="right">
                                    {hero.estimatedPriceTotalMax != null &&
                                        <PriceRangeValue>{formatFilterPrice(hero.estimatedPriceTotalMax)} zł</PriceRangeValue>}
                                    {hero.estimatedPricePerSqmMax != null &&
                                        <PriceRangeSub>{formatFilterPrice(hero.estimatedPricePerSqmMax)} zł / m²</PriceRangeSub>}
                                    <PriceRangeSubLabel>Maksymalna cena sprzedaży</PriceRangeSubLabel>
                                </PriceMinMaxGroup>
                            </PriceMinMaxRow>
                            <PriceBarTrack>
                                <PriceBarDot side="left" />
                                <PriceBarDot side="right" />
                            </PriceBarTrack>
                        </PriceRangesBlock>
                    }
                </HeroMetricsRow>
            }
        </HeroCard>
    );
};

export default ValuationHeroCard;
